import useFetchProjectSolarPotential from '@/api/mutations/project/useFetchProjectSolarPotential';
import { useStartSolarGeometryGeneration } from '@/api/mutations/project/useStartSolarGeometryGeneration';
import { useCreateSolarDesign } from '@/api/mutations/project/useCreateSolarDesign';
import projectKeys from '@/api/queries/project/keyFactory';
import useGetProjectSolarPotential from '@/api/queries/project/useGetProjectSolarPotential';
import { useSolarDesignerState } from '@/api/queries/project/useSolarDesignerState';
import {
  Alert,
  AnchorButton,
  Badge,
  Button,
  Card,
  ContextMenuDropdown,
  H3,
  SkeletonLoader,
  Text,
} from '@payaca/ambrosia';
import { type SolarLatLng } from '@payaca/components/solarRoofView/SolarRoofView';
import { AppliancesPermissions } from '@payaca/permissions/appliances/appliances.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate } from 'date-fns';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import useSolarEquipmentReadiness from '@/api/queries/appliances/useSolarEquipmentReadiness';
import { usePermissions } from '@/hooks/usePermissions';
import { GLOW_SETTINGS_PATH } from '@/ui/components/companySettings/estimateSettingsRoutes';
import { BrandedCardTitle } from './BrandedCardTitle';
import { chooseDesignSource } from '../../../solarDesignerPage/solarDesigner/chooseDesignSource';
import { SolarDesignDraftList } from './SolarDesignDraftList';
import { solarDesignDraftsKey } from '@/api/queries/project/useSolarDesignDrafts';
import {
  SITE_LOCATION_POLL_MS,
  resolveSiteLocationReadiness,
} from './siteLocationReadiness';

type GlowSolarCardProps = {
  projectId: string;
  /** The site address's geocoded point; drives the confirm-location gate. */
  siteAddressCentre?: SolarLatLng;
  /** Identity of the site address the centre belongs to. */
  siteAddressKey: string;
};

const GlowCardTitle: FC = () => (
  <div className="flex min-w-0 items-center gap-3">
    <div
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-yellow-50 text-yellow-600"
    >
      <UntitledIcon name="sun.3" className="h-5 w-5" />
    </div>
    <H3>
      <BrandedCardTitle brand="Glow" descriptor="Solar" />
    </H3>
  </div>
);

const formatImageryDate = (isoDate: string) => {
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime())
    ? isoDate
    : formatDate(parsed, 'MMMM yyyy');
};

/** HIGH = aerial imagery at ≤10 cm/pixel — trustworthy tracing. */
const imageryQualityPill = (
  quality: string
): { label: string; colour: 'green' | 'gray' } =>
  quality === 'HIGH'
    ? { label: 'High detail', colour: 'green' }
    : { label: 'Standard detail', colour: 'gray' };

const reanalyseBlockedReason = (state: {
  hasSiteCentre: boolean;
  equipmentMissing: boolean;
}): string | undefined => {
  if (!state.hasSiteCentre) return 'The project needs a geocoded site address.';
  if (state.equipmentMissing) return 'Set up your solar equipment first.';
  return undefined;
};

const SolarEquipmentWarning: FC = () => {
  const { userHasRequiredPermission } = usePermissions();
  const canManage = userHasRequiredPermission([
    AppliancesPermissions.MANAGE_APPLIANCES,
  ]);
  return (
    <Alert colour="yellow">
      <div className="space-y-2">
        <Text>
          <strong>Set up your solar equipment first</strong>
        </Text>
        <Text>
          A design needs to know your preferred solar panels and inverters to
          lay out panels on the roof and price the system.
        </Text>
        {canManage ? (
          <AnchorButton
            as={Link}
            to={GLOW_SETTINGS_PATH}
            variant="secondary"
            size="sm"
          >
            Open settings
          </AnchorButton>
        ) : (
          <Text>
            Ask someone who manages your account to add your preferred solar
            panels and inverters in settings.
          </Text>
        )}
      </div>
    </Alert>
  );
};

/**
 * Design tab card for Glow, the native solar design product. Confirm-location
 * gate: THE USER CONFIRMS THE BUILDING POINT BEFORE ANY ANALYSIS RUNS
 * (Google's building lookup can snap the raw geocode to a neighbour's roof).
 */
const GlowSolarCard: FC<GlowSolarCardProps> = (props) => {
  const { projectId, siteAddressCentre, siteAddressKey } = props;

  const history = useHistory();
  const { solarPotential: cachedPotential, isLoading: isLoadingPotential } =
    useGetProjectSolarPotential(projectId);
  const designerState = useSolarDesignerState(projectId);
  const draft = designerState.data?.projectById.solarDesignDraft;
  const generation = designerState.data?.projectById.solarGeometryGeneration;
  const solarPotential = chooseDesignSource(cachedPotential, draft, generation);
  const isLoading = isLoadingPotential || designerState.isLoading;
  const openAnalysisConfirmation = useCallback(
    () => history.push(`/projects/${projectId}/solar-designer?analyse=1`),
    [history, projectId]
  );
  const { mutate: fetchSolarPotential, isPending: isFetching } =
    useFetchProjectSolarPotential();
  const createDesign = useCreateSolarDesign(
    projectId,
    designerState.data?.projectById.publicId
  );
  const startGeneration = useStartSolarGeometryGeneration(projectId, projectId);
  const { missingPanel, missingInverter } = useSolarEquipmentReadiness();
  const equipmentMissing = missingPanel || missingInverter;
  const equipmentWarning = equipmentMissing ? <SolarEquipmentWarning /> : null;
  const onDrawByHand = async () => {
    const designId = crypto.randomUUID();
    try {
      await createDesign.mutateAsync({
        designId,
        name: 'Hand-drawn roof',
        description: '',
        basis: 'MANUAL',
      });
      history.push(`/projects/${projectId}/solar-designer?design=${designId}`);
    } catch {
      /* The mutation error renders in the card. */
    }
  };

  /**
   * A fresh analysis at the confirmed point. `forceRefresh` alone is not
   * enough: the potential cache is only skipped when the generation carries a
   * location, so the point has to travel with it. The designer owns the
   * progress and result UI, so hand the user over to it.
   */
  const onReanalyseRoof = () => {
    startGeneration.mutate(
      {
        location: siteAddressCentre,
        retry: generation?.status === 'FAILED',
        forceRefresh: true,
      },
      {
        onSuccess: (data) =>
          history.push(
            `/projects/${projectId}/solar-designer?analyse=1&run=${data.startSolarGeometryGeneration.id}`
          ),
      }
    );
  };

  const blockedReason = reanalyseBlockedReason({
    hasSiteCentre: !!siteAddressCentre,
    equipmentMissing,
  });

  const reanalyseMenu = (
    <ContextMenuDropdown
      size="sm"
      variant="secondary"
      className="aspect-square shrink-0"
      label="Glow actions"
      menuItems={[
        {
          icon: 'refresh-cw-01',
          label: 'Re-analyse the roof',
          onClick: () => onReanalyseRoof(),
          isProcessing: startGeneration.isPending,
          disabled:
            !siteAddressCentre || startGeneration.isPending || equipmentMissing,
          disabledReasons: blockedReason ? [blockedReason] : undefined,
        },
      ]}
    />
  );

  const queryClient = useQueryClient();

  // A finished analysis publishes a new source version, which flips isStale on
  // every saved design. Only this card watches the generation, so the list is
  // told from here rather than polling for it.
  const wasGeneratingRef = useRef(false);
  useEffect(() => {
    const status = generation?.status;
    if (status === 'QUEUED' || status === 'RUNNING') {
      wasGeneratingRef.current = true;
      return;
    }
    if (status === 'READY' && wasGeneratingRef.current) {
      wasGeneratingRef.current = false;
      void queryClient.invalidateQueries({
        queryKey: solarDesignDraftsKey(projectId),
      });
    }
  }, [generation?.status, projectId, queryClient]);

  // Wait for the geocode rather than reading a new address as having no
  // location.
  const [addressSeenAt, setAddressSeenAt] = useState(() => Date.now());
  const [msSinceAddressSeen, setMsSinceAddressSeen] = useState(0);
  useEffect(() => {
    setAddressSeenAt(Date.now());
    setMsSinceAddressSeen(0);
  }, [siteAddressKey]);

  const locationReadiness = resolveSiteLocationReadiness({
    hasCoordinates: !!siteAddressCentre,
    msSinceAddressSeen,
  });
  const isLocating = locationReadiness === 'pending';

  useEffect(() => {
    if (!isLocating) return undefined;

    const timer = window.setInterval(() => {
      setMsSinceAddressSeen(Date.now() - addressSeenAt);
      void queryClient.invalidateQueries({
        queryKey: projectKeys.overview(projectId),
      });
    }, SITE_LOCATION_POLL_MS);

    return () => window.clearInterval(timer);
  }, [isLocating, addressSeenAt, projectId, queryClient]);

  // Held, not dropped — analysing without a point would bypass the
  // confirm-location gate.
  const [isClickHeld, setIsClickHeld] = useState(false);
  const onCheckSolarPotential = () => {
    if (locationReadiness === 'pending') {
      setIsClickHeld(true);
      return;
    }
    if (locationReadiness === 'ready') {
      openAnalysisConfirmation();
      return;
    }
    fetchSolarPotential({ projectId });
  };

  useEffect(() => {
    if (!isClickHeld || locationReadiness === 'pending') return;

    setIsClickHeld(false);
    if (locationReadiness === 'ready') {
      openAnalysisConfirmation();
      return;
    }
    // No point after waiting: falls back to the direct, unconfirmed fetch.
    fetchSolarPotential({ projectId });
  }, [
    isClickHeld,
    locationReadiness,
    openAnalysisConfirmation,
    fetchSolarPotential,
    projectId,
  ]);

  const manualRoofRequired =
    generation?.recoveryReason === 'NO_USABLE_FACES' ||
    solarPotential?.roofRecoveryReason === 'NO_USABLE_FACES' ||
    (!solarPotential &&
      cachedPotential?.roofRecoveryReason === 'NO_USABLE_FACES');
  const usableSolarPotential =
    solarPotential?.available &&
    solarPotential.centre &&
    !solarPotential.roofRecoveryReason
      ? { ...solarPotential, centre: solarPotential.centre }
      : undefined;

  const geometry = usableSolarPotential?.geometry;
  const geometryFaceCount =
    geometry != null
      ? (geometry.stats?.faceCount ?? geometry.faces.length)
      : null;

  let body: ReactNode;
  if (isLoading) {
    body = (
      <Card>
        <Card.Body>
          <SkeletonLoader className="h-16 w-full rounded-md" />
        </Card.Body>
      </Card>
    );
  } else if (designerState.isError && !designerState.data) {
    body = (
      <Card>
        <Card.Body className="space-y-4">
          <div className="space-y-2">
            <GlowCardTitle />
            <Text>The saved design could not be loaded.</Text>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="shrink-0 whitespace-nowrap"
            onClick={() => void designerState.refetch()}
          >
            Try again
          </Button>
        </Card.Body>
      </Card>
    );
  } else if ((!solarPotential && !manualRoofRequired) || isFetching) {
    body = (
      <Card>
        <Card.Body className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <GlowCardTitle />
              <Text>
                Confirm the building, then check satellite imagery to start a
                solar design.
              </Text>
            </div>
            <Button
              size="sm"
              variant="secondary"
              className="shrink-0 whitespace-nowrap"
              onClick={onCheckSolarPotential}
              isProcessing={isFetching || isLocating}
              disabled={isFetching || equipmentMissing}
            >
              {isLocating ? (
                'Locating the address'
              ) : (
                <>
                  <UntitledIcon name="search-lg.3" className="h-5 w-5" />
                  Check solar potential
                </>
              )}
            </Button>
          </div>
          {equipmentWarning}
          {draft?.basis === 'MANUAL' && (
            <SolarDesignDraftList
              key={projectId}
              projectId={projectId}
              projectPublicId={designerState.data?.projectById.publicId}
              siteAddressCentre={siteAddressCentre}
              canCreate={!equipmentMissing}
            />
          )}
        </Card.Body>
      </Card>
    );
  } else if (!usableSolarPotential) {
    body = (
      <Card>
        <Card.Body className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <GlowCardTitle />
              <Text>
                {manualRoofRequired
                  ? 'Satellite imagery is available, but no usable roof outline could be generated.'
                  : 'No usable satellite data for this property.'}
              </Text>
              <Text>
                {siteAddressCentre &&
                  manualRoofRequired &&
                  'Draw the roof by hand and check obstacles against your survey.'}
                {siteAddressCentre &&
                  !manualRoofRequired &&
                  'Check the building location and try again, or draw the roof by hand.'}
                {!siteAddressCentre &&
                  'The project needs a geocoded site address before the roof can be analysed again or drawn by hand.'}
              </Text>
            </div>
            {siteAddressCentre && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  className="shrink-0 whitespace-nowrap"
                  onClick={openAnalysisConfirmation}
                >
                  <UntitledIcon name="marker-pin-01.3" className="h-5 w-5" />
                  Check the building location
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="shrink-0 whitespace-nowrap"
                  onClick={() => void onDrawByHand()}
                  isProcessing={createDesign.isPending}
                  disabled={createDesign.isPending || equipmentMissing}
                >
                  <UntitledIcon name="edit-05.3" className="h-5 w-5" />
                  Draw the roof by hand
                </Button>
                {reanalyseMenu}
              </div>
            )}
          </div>
          {equipmentWarning}
          {createDesign.isError && (
            <Text as="div" role="alert">
              The design could not be created. Please try again.
            </Text>
          )}
          <SolarDesignDraftList
            key={projectId}
            projectId={projectId}
            projectPublicId={designerState.data?.projectById.publicId}
            siteAddressCentre={siteAddressCentre}
            canCreate={!equipmentMissing}
          />
        </Card.Body>
      </Card>
    );
  } else {
    body = (
      <Card>
        <Card.Body className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <GlowCardTitle />
            <div className="shrink-0">{reanalyseMenu}</div>
          </div>
          {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-4">
            {usableSolarPotential.maxPanelsCount != null && (
              <div>
                <Text level="supporting">Max panels</Text>
                <Text>{usableSolarPotential.maxPanelsCount}</Text>
              </div>
            )}
            {usableSolarPotential.maxSunshineHoursPerYear != null && (
              <div>
                <Text level="supporting">Max sunshine</Text>
                <Text>
                  {Math.round(
                    usableSolarPotential.maxSunshineHoursPerYear
                  ).toLocaleString('en-GB')}{' '}
                  hrs/yr
                </Text>
              </div>
            )}
            {usableSolarPotential.imageryDate && (
              <div>
                <Text level="supporting">Imagery date</Text>
                <Text>
                  {formatImageryDate(usableSolarPotential.imageryDate)}
                </Text>
              </div>
            )}
            {usableSolarPotential.imageryQuality && (
              <div>
                <Text level="supporting">Imagery quality</Text>
                <Badge
                  variant="soft"
                  colour={
                    imageryQualityPill(usableSolarPotential.imageryQuality)
                      .colour
                  }
                  size="sm"
                  rounded
                >
                  {
                    imageryQualityPill(usableSolarPotential.imageryQuality)
                      .label
                  }
                </Badge>
              </div>
            )}
          </div>

          {geometry && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="soft" colour="green" size="sm" rounded>
                <UntitledIcon name="check" className="h-3 w-3" />
                Precise roof model
              </Badge>
              <Text as="span" level="supporting">
                {geometryFaceCount} face{geometryFaceCount === 1 ? '' : 's'}
              </Text>
            </div>
          )}

          {equipmentWarning}
          <SolarDesignDraftList
            key={projectId}
            projectId={projectId}
            projectPublicId={designerState.data?.projectById.publicId}
            source={usableSolarPotential}
            canCreate={!equipmentMissing}
          />
        </Card.Body>
      </Card>
    );
  }

  return body;
};

export default GlowSolarCard;
